/**
 * `<ErrorState>` — the one inline error surface for failed data loads.
 *
 * Replaces the hand-rolled `<Alert variant="destructive"><AlertDescription>…`
 * blocks scattered across list pages, and adds an optional Retry button
 * (wire it to the query's `refetch`). Keep a friendly, translated message —
 * don't pass raw `error.message`.
 */

import { Alert, AlertDescription, AlertTitle, Button } from "@cometchat-ai/ui-dashboard";
import { RotateCcw } from "./icons";
import type { ReactNode } from "react";

interface ErrorStateProps {
  /** Optional heading; omit for a single-line message. */
  title?: ReactNode;
  /** The user-facing message (already translated). */
  message: ReactNode;
  /** When provided, renders a Retry button that calls this (e.g. `refetch`). */
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
  "data-testid"?: string;
}

export function ErrorState({
  title,
  message,
  onRetry,
  retryLabel = "Retry",
  className,
  ...rest
}: ErrorStateProps) {
  return (
    <Alert
      variant="destructive"
      role="alert"
      className={className}
      data-testid={rest["data-testid"]}
    >
      {title ? <AlertTitle>{title}</AlertTitle> : null}
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <span>{message}</span>
        {onRetry ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="h-8 shrink-0 gap-1.5 rounded-lg"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            {retryLabel}
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
